import { Box, Center, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';

import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { truncateUploadFileName } from 'packages/common-frontend/utils';

export type UploadFileExtension =
  | 'csv'
  | 'xlsx'
  | 'json'
  | 'pdf'
  | 'txt'
  | 'zip'
  | 'xlsm'
  | 'xlsb';

type Props = {
  allowedExtensions: UploadFileExtension[];
  isDisabled: boolean;
  setFieldValue: (value: FileWithPath) => void;
  accept: any;
};
const UploadDataTapeDropzone = ({
  allowedExtensions,
  isDisabled,
  setFieldValue,
  accept,
}: Props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept,
    multiple: false,
    onDrop: useCallback((files: FileWithPath[]) => setFieldValue(files[0]), [
      setFieldValue,
    ]),
    disabled: isDisabled,
  });

  const fileSelected = !_.isEmpty(acceptedFiles) || !_.isEmpty(fileRejections);

  const acceptedFileItems = acceptedFiles.map((file: FileWithPath) =>
    truncateUploadFileName(file.path),
  );

  const fileRejectionItems = fileRejections.map(
    ({ file }: { file: FileWithPath }) => truncateUploadFileName(file.path),
  );

  const bgColor = useColorModeValue(colors.background[50], 'inherit');
  const selectedBgColor = useColorModeValue(colors.background[200], 'inherit');
  const fontColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box width="100%" cursor="pointer">
      <Center
        border="1px"
        borderColor="gray.100"
        borderRadius="md"
        bgColor={fileSelected ? selectedBgColor : bgColor}
        minHeight="200px"
        flexDirection="column"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} />
        {!_.isEmpty(acceptedFiles) && (
          <Stack alignItems="center">
            <Text color={fontColor} pt={4} fontWeight="semibold" fontSize="xl">
              {acceptedFileItems.join(', ')}
            </Text>
          </Stack>
        )}
        {!_.isEmpty(fileRejections) && (
          <Stack alignItems="center">
            <FaIcon icon="circle-xmark" color="red.300" boxSize={10} />
            <Text color={fontColor} pt={4} fontWeight="semibold" fontSize="xl">
              {`${fileRejectionItems.join(', ')} failed`}
            </Text>
            <Text color={fontColor} fontSize="sm">
              Try again
            </Text>
          </Stack>
        )}
        {!fileSelected && (
          <Stack alignItems="center">
            <FaIcon icon="file-arrow-up" color="gray.400" boxSize={10} />
            <Text color={fontColor} pt={4} fontWeight="semibold" fontSize="xl">
              {`Drop a ${allowedExtensions
                .join('/')
                .toUpperCase()} file to upload`}
            </Text>
            <Text color={fontColor} fontSize="sm">
              or browse here
            </Text>
          </Stack>
        )}
      </Center>
    </Box>
  );
};

export default UploadDataTapeDropzone;
